/**
 * Collapsed group markers (WI #5531 activity grouping): same-row activities
 * that cannot render sensibly side by side — overlapping hexagons
 * (capacity-overflow stacks, same-date pile-ups) or runs too tight for any
 * member to show a readable label — render as ONE stacked-hexagon badge with
 * the member count, so no activity is ever invisible, unreachable or
 * anonymous. Clicking the marker fans the members out inside their band, or
 * — where the band has no room for that — lists them next to the marker
 * (interaction router + layout engine + MapGroupList); this painter only draws
 * the collapsed state.
 *
 * Detection lives in the layout engine (`scene.layout.groups`); the members'
 * own hexagons/labels/banners are suppressed via `scene.layout.collapsedIds`
 * in their painters. The marker face splits into equal vertical bands, one
 * per DISTINCT value among the members (one value → solid, two → halves,
 * three → thirds …):
 *  - regular view → one band per distinct responsible function, in stable
 *    member order, using the member hexagons' colour source (`functionColors`);
 *  - status view (`isHidingActivityColors`) → one band per distinct status
 *    mode, in canonical lifecycle order, using the same status palette the
 *    individual hexagons use (`hexagonStatusFill`) — an all-`unplanned` group
 *    yields a solid white marker, matching its neutral look.
 * A selection ring shows when ANY collapsed member is selected — red while a
 * move leaves one of them behind — the hover ring mirrors the hexagon hover
 * treatment (also while the member list is open).
 * The marker's centre stays inside its band (`markerY`).
 */
import {
  cssVar,
  hexagonLabelColor,
  hexagonStatusFill,
  modeOf,
} from '../../draw/palette';
import { hexPath } from '../../draw/shapes';
import { withGroupOpacity } from '../../engine/groupOpacity';
import { markerY, type LayoutGroup } from '../../layout/activityLayout';
import type { SceneContext } from '../../engine/SceneContext';
import type { ItemMode } from '@/common';

// Offset of the back hexagon peeking out behind the front one (the "stack").
const STACK_DX = 4;
const STACK_DY = -4;

// Member-count badge at the marker's top-right corner (WI #5557): a small disc
// carrying the count, so it reads as "how many activities" rather than as a
// regular activity's in-hexagon sequence number. Sized for two digits.
// Pill badge straddling the hexagon's top-right vertex — half on the face, half
// out — so it neither bulges far past the silhouette nor swallows the small
// (~30px) hexagon. A stadium shape (circle for one digit, widening only
// horizontally for two) lets the count use a readable 11px without growing
// taller. BADGE_R is the pill's half-height / end-cap radius.
// (Prior compact-circle version: 9px font, R 8, plain arc — kept for reference.)
const BADGE_FONT = '700 11px Roboto, sans-serif';
const BADGE_R = 8;
const BADGE_PAD_X = 4;
const BADGE_CX = 11;
const BADGE_CY = -9;

export function drawGroupMarkers(
  ctx: CanvasRenderingContext2D,
  scene: SceneContext,
): void {
  const groups = scene.layout?.groups ?? [];
  if (!groups.length) {
    return;
  }
  const { map } = scene;

  // Badge-text settings, set once. The alphabetic-baseline probe yields the
  // offset that vertically centres the digits inside the badge disc.
  ctx.font = BADGE_FONT;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  const probe = ctx.measureText('0');
  const badgeTextCY =
    (probe.actualBoundingBoxAscent - probe.actualBoundingBoxDescent) / 2;

  for (const group of groups) {
    if (group.x < -100 || group.x > scene.width + 100) {
      continue;
    }
    // The marker rides the mean of its members' ANIMATED Ys, so it glides
    // with a converging cluster instead of popping to the settled centre —
    // kept inside its band (markerY).
    const y = markerY(group, (id) => scene.layout?.y(id) ?? group.y);

    // Filter dimming follows the content: only when EVERY member is filtered
    // out does the marker dim (a single matching member keeps it readable).
    const allShadowed = group.memberIds.every((id) =>
      map.isShadowedActivityId(id),
    );
    if (allShadowed) {
      withGroupOpacity(ctx, 0.2, (g) => {
        g.font = BADGE_FONT;
        g.textAlign = 'center';
        g.textBaseline = 'alphabetic';
        drawMarker(g, scene, group, y, badgeTextCY);
      });
    } else {
      drawMarker(ctx, scene, group, y, badgeTextCY);
    }
  }
}

/** Draw one stacked-hexagon marker at full opacity (dimming is the caller's). */
function drawMarker(
  ctx: CanvasRenderingContext2D,
  scene: SceneContext,
  group: LayoutGroup,
  y: number,
  badgeTextCY: number,
): void {
  const { map } = scene;
  // An open member list rings its marker like a hover, tying the two together.
  const hovered =
    scene.layout?.hoveredGroupKey === group.key ||
    scene.layout?.listedGroupKey === group.key;
  const selected = group.memberIds.some(
    (id) => map.selectedId === id || map.selectedIds.includes(id),
  );
  // A move in progress leaves a member here — the user may not move it.
  const leftBehind = group.memberIds.some((id) =>
    map.leftBehindActivityIds.includes(id),
  );
  const stroke = cssVar('--color-gray-500', '#667085');

  ctx.save();
  ctx.translate(group.x, y);

  // Back hexagon — the "several activities here" stack hint.
  ctx.save();
  ctx.translate(STACK_DX, STACK_DY);
  ctx.fillStyle = '#FFFFFF';
  ctx.fill(hexPath);
  ctx.lineWidth = 2;
  ctx.strokeStyle = stroke;
  ctx.lineJoin = 'round';
  ctx.stroke(hexPath);
  ctx.restore();

  // Hover ring (hexagon parity: #ccc, width 8; selection takes precedence).
  if (hovered && !selected && !leftBehind) {
    ctx.save();
    ctx.lineWidth = 8;
    ctx.strokeStyle = '#cccccc';
    ctx.lineJoin = 'round';
    ctx.stroke(hexPath);
    ctx.restore();
  }

  // Selection ring when any collapsed member is selected (hexagon parity),
  // red (at 0.9) while a move leaves one of them behind.
  if (selected || leftBehind) {
    ctx.save();
    ctx.globalAlpha = leftBehind ? 0.9 : 0.6;
    ctx.lineWidth = 8;
    ctx.strokeStyle = leftBehind
      ? cssVar('--color-error-500', '#F04438')
      : cssVar('--color-bluelight-500', '#0BA5EC');
    ctx.stroke(hexPath);
    ctx.restore();
  }

  // Search-hovered ring (hexagon parity): a hovered search result hidden in
  // this group lights up its marker, as its own hexagon would.
  if (
    map.hoveredSearchActivity &&
    group.memberIds.includes(map.hoveredSearchActivity)
  ) {
    ctx.save();
    ctx.globalAlpha = 0.8;
    ctx.lineWidth = 10;
    ctx.lineJoin = 'round';
    ctx.strokeStyle = cssVar('--color-primary-400', '#6F8DB6');
    ctx.shadowColor = cssVar('--color-primary-300', '#86A2C8');
    ctx.shadowBlur = 8;
    ctx.stroke(hexPath);
    ctx.restore();
  }

  // Front hexagon: equal vertical bands, one per distinct member value
  // (responsible function in regular view, status mode in status view), then
  // the gray outline. The member count is NOT drawn on the face — it rides a
  // separate top-right badge (below). A single distinct value (or an
  // all-unplanned status group) paints a solid face.
  const segments = markerSegments(scene, group);
  if (segments.length <= 1) {
    ctx.fillStyle = segments[0]?.color ?? '#FFFFFF';
    ctx.fill(hexPath);
  } else {
    ctx.save();
    ctx.clip(hexPath);
    const bandW = (HEX_HALF * 2) / segments.length;
    segments.forEach((segment, i) => {
      ctx.fillStyle = segment.color;
      // +1px overdraw avoids hairline seams between bands (the clip bounds it).
      ctx.fillRect(-HEX_HALF + i * bandW, -HEX_HALF, bandW + 1, HEX_HALF * 2);
    });
    ctx.restore();
  }
  ctx.lineWidth = 2;
  ctx.strokeStyle = stroke;
  ctx.lineJoin = 'round';
  ctx.stroke(hexPath);

  // Member-count badge at the top-right corner (WI #5557). Its own white disc
  // with the hexagon's gray outline keeps the count visually distinct from a
  // regular activity's in-hexagon sequence number, regardless of the band
  // colours behind it. The disc is sized for two digits.
  const count = String(group.memberIds.length);
  // Stadium: a circle (w === h) for one digit, widening horizontally for more.
  const h = BADGE_R * 2;
  const w = Math.max(h, ctx.measureText(count).width + BADGE_PAD_X * 2);
  ctx.beginPath();
  ctx.roundRect(BADGE_CX - w / 2, BADGE_CY - BADGE_R, w, h, BADGE_R);
  ctx.fillStyle = '#FFFFFF';
  ctx.fill();
  ctx.lineWidth = 2;
  ctx.strokeStyle = stroke;
  ctx.lineJoin = 'round';
  ctx.stroke();
  ctx.fillStyle = cssVar('--color-gray-800', '#1D2939');
  ctx.fillText(count, BADGE_CX, BADGE_CY + badgeTextCY);

  ctx.restore();
}

// Hexagon bounding half-extent for the band fill (the path spans ~±15.5 × ±14).
const HEX_HALF = 16;

// Canonical lifecycle order for status bands, left → right. Distinct status
// modes are sorted into this fixed order regardless of member iteration order
// (function view keeps its member-order behaviour). Mirrors the lifecycle the
// individual status hexagons read left-to-right.
const STATUS_RANK: Record<ItemMode, number> = {
  unplanned: 0,
  planned: 1,
  underway: 2,
  'underway-red': 3,
  'underway-yellow': 4,
  'underway-green': 5,
  done: 6,
  // `notRequired` can't arise from a MapActivity (its state is never
  // `notRequired`), but ItemMode includes it — rank it last for completeness.
  notRequired: 7,
};

/**
 * Bands for the marker face — one per DISTINCT member value, equal widths.
 *
 * Regular view: one band per distinct responsible function, in stable member
 * order (PO 2026-06-11). The colour source is the members' own hexagon fill
 * (`functionColors(responsible)`, black default for unassigned).
 *
 * Status view (`isHidingActivityColors`): one band per distinct status mode,
 * in canonical lifecycle order (`STATUS_RANK`), mirroring how regular view
 * shows the members' functions. Colours come from `hexagonStatusFill(mode)` —
 * the same source individual hexagons use in status view — and the count colour
 * from `hexagonLabelColor(mode)` (white on the dark `done` fill). `unplanned`
 * is a white band, so an all-`unplanned` group yields a solid white marker.
 * Mirrored members get no special-casing: each contributes its
 * `modeOf(state, status)` like any other member.
 */
function markerSegments(
  scene: SceneContext,
  group: LayoutGroup,
): { color: string; label: string }[] {
  const { map, io } = scene;
  if (map.isHidingActivityColors) {
    const modes = new Set<ItemMode>();
    for (const id of group.memberIds) {
      const { state, status } = io.activity(id);
      modes.add(modeOf(state, status));
    }

    return [...modes]
      .sort((a, b) => STATUS_RANK[a] - STATUS_RANK[b])
      .map((mode) => ({
        color: hexagonStatusFill(mode),
        label: hexagonLabelColor(mode),
      }));
  }
  const seen = new Set<string | null>();
  const segments: { color: string; label: string }[] = [];
  for (const id of group.memberIds) {
    // `|| null` folds '' and undefined into the unassigned bucket.
    const fnId = io.activity(id).responsible || null;
    if (seen.has(fnId)) {
      continue;
    }
    seen.add(fnId);
    const { color, label } = io.functionColors(fnId);
    segments.push({ color, label });
  }

  return segments;
}

export { markerSegments };
